'use client'

/**
 * /telegram — Landing page for FairFares Telegram channel followers.
 *
 * Shown when someone taps a link from the Telegram channel (UTM-tagged).
 * Goals:
 *  1. Welcome Telegram visitors by name/context
 *  2. Explain free vs Plus account
 *  3. Conversion CTA to sign up
 *  4. Preview the 3 best current deals
 */

import { useState, useEffect } from 'react'
import Link from 'next/link'
import AuthModal from '@/components/AuthModal'
import { useAuth } from '@/context/AuthContext'
import Navbar from '@/components/Navbar'

// UTM params for all outbound links from this page
const UTM = '?utm_source=telegram&utm_medium=channel&utm_campaign=landing_page'

const FREE_FEATURES = [
  'Beperkt aantal deals',
  '1 thuisluchthaven',
  'Geen creditcard nodig',
]

const PLUS_FEATURES = [
  'Alle deals, directe toegang',
  '5 thuisluchthavens',
  'Dagelijkse e-mail digest',
  'Prijshistorie & filters',
]

function CheckIcon() {
  return (
    <svg className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

function PreviewDealCard({ deal }) {
  const origin      = deal.origin ?? '?'
  const destination = deal.destination ?? '?'

  function formatDate(iso) {
    if (!iso) return ''
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
  }

  return (
    <Link
      href={`/deals/${deal.id}${UTM}`}
      className="block bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
      onClick={() => window.plausible?.('TelegramPreviewDealClick', { props: { deal: deal.id } })}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-bold text-gray-900">{origin} → {destination}</div>
          {deal.airline && <div className="text-xs text-gray-400 mt-0.5">{deal.airline}</div>}
        </div>
        {deal.discountPct && (
          <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shrink-0">
            -{deal.discountPct}%
          </span>
        )}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-bold text-gray-900">€{deal.price}</span>
        {deal.normalPrice && (
          <span className="text-xs text-gray-400 line-through">€{deal.normalPrice}</span>
        )}
        {deal.normalPrice && deal.price && (
          <span className="text-xs text-green-600 font-semibold">
            bespaar €{deal.normalPrice - deal.price}
          </span>
        )}
      </div>
      {deal.departureDate && (
        <div className="text-xs text-gray-500 mt-1">
          ✈ {formatDate(deal.departureDate)}
          {deal.returnDate && ` – ${formatDate(deal.returnDate)}`}
        </div>
      )}
    </Link>
  )
}

export default function TelegramLandingPage() {
  const { currentUser }           = useAuth()
  const [showAuth, setShowAuth]   = useState(false)
  const [deals, setDeals]         = useState([])
  const [dealsLoading, setDealsLoading] = useState(true)

  useEffect(() => {
    // Track this page view in Plausible
    window.plausible?.('TelegramLanding')

    fetch('/api/deals?limit=3&sort=score')
      .then(r => r.json())
      .then(data => setDeals((data.deals ?? []).slice(0, 3)))
      .catch(() => {})
      .finally(() => setDealsLoading(false))
  }, [])

  function handleSignupCTA(source) {
    window.plausible?.('TelegramSignupCTA', { props: { source } })
    setShowAuth(true)
  }

  return (
    <>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} initialMode="signup" />}

      <Navbar />

      <main className="min-h-screen bg-gray-50 px-4 py-10">
        <div className="mx-auto max-w-lg space-y-10">

          {/* ── Hero ─────────────────────────────────────────────────────── */}
          <header className="text-center">
            <div className="text-5xl mb-4">✈️</div>
            <span className="inline-block bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
              🆕 Nieuw kanaal
            </span>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 leading-tight">
              Welkom FairFares<br />Telegram-volger
            </h1>
            <p className="mt-4 text-gray-500 text-base leading-relaxed max-w-sm mx-auto">
              We hebben ons Telegram-kanaal net gelanceerd — wees een van de eersten die meedoet
              en ontvang dagelijks de beste vliegdeals direct op je telefoon.
            </p>
          </header>

          {/* ── Primary signup CTA (only for guests) ─────────────────────── */}
          {!currentUser ? (
            <div className="bg-blue-600 rounded-2xl p-6 text-center text-white shadow-lg shadow-blue-200">
              <p className="font-bold text-xl mb-1">Maak gratis een account aan</p>
              <p className="text-blue-100 text-sm mb-5">
                Geen creditcard nodig &mdash; klaar in 30 seconden.
              </p>
              <button
                onClick={() => handleSignupCTA('hero')}
                className="bg-white text-blue-700 font-bold px-8 py-3 rounded-xl hover:bg-blue-50 active:bg-blue-100 transition-colors text-sm"
              >
                Account aanmaken →
              </button>
              <p className="mt-4 text-blue-200 text-xs">
                Al een account?{' '}
                <button
                  onClick={() => { window.plausible?.('TelegramSigninCTA'); setShowAuth(true) }}
                  className="underline hover:text-white"
                >
                  Inloggen
                </button>
              </p>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center">
              <p className="text-green-700 font-semibold text-lg">✓ Je bent ingelogd</p>
              <Link
                href={`/${UTM}`}
                className="mt-2 inline-block text-sm text-blue-600 hover:underline font-medium"
              >
                Bekijk alle deals →
              </Link>
            </div>
          )}

          {/* ── Free vs Plus comparison ───────────────────────────────────── */}
          <section>
            <h2 className="text-center text-xl font-bold text-gray-900 mb-5">Gratis vs Plus</h2>
            <div className="grid grid-cols-2 gap-4">

              {/* Free */}
              <div className="bg-white rounded-2xl border-2 border-gray-200 p-5 flex flex-col">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Gratis</p>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold text-gray-900">€0</span>
                </div>
                <ul className="space-y-2 flex-1">
                  {FREE_FEATURES.map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckIcon />
                      <span className="text-xs text-gray-600 leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>
                {!currentUser && (
                  <button
                    onClick={() => handleSignupCTA('free_card')}
                    className="mt-5 block w-full text-center border border-gray-300 text-gray-700 text-xs font-semibold rounded-xl py-2 hover:bg-gray-50 transition-colors"
                  >
                    Begin gratis
                  </button>
                )}
              </div>

              {/* Plus */}
              <div className="bg-white rounded-2xl border-2 border-blue-600 p-5 flex flex-col relative shadow-lg shadow-blue-100">
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
                  Populairste keuze
                </span>
                <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-1">Plus</p>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold text-gray-900">€4,99</span>
                  <span className="text-xs text-gray-400">/mo</span>
                </div>
                <ul className="space-y-2 flex-1">
                  {PLUS_FEATURES.map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckIcon />
                      <span className="text-xs text-gray-600 leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  href={`/pricing${UTM}`}
                  onClick={() => window.plausible?.('TelegramUpgradeCTA')}
                  className="mt-5 block text-center bg-blue-600 text-white text-xs font-bold rounded-xl py-2 hover:bg-blue-700 transition-colors"
                >
                  Upgrade naar Plus
                </Link>
              </div>

            </div>
            <p className="text-center text-xs text-gray-400 mt-3">
              Prijzen incl. 21% BTW &bull; Maandelijks opzegbaar
            </p>
          </section>

          {/* ── Deal preview ──────────────────────────────────────────────── */}
          <section>
            <h2 className="text-center text-xl font-bold text-gray-900 mb-2">
              Actuele deals
            </h2>
            <p className="text-center text-sm text-gray-500 mb-5">
              Een voorproefje van wat er vandaag beschikbaar is
            </p>

            {dealsLoading ? (
              <div className="space-y-3">
                {[0, 1, 2].map(i => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse h-24" />
                ))}
              </div>
            ) : deals.length > 0 ? (
              <div className="space-y-3">
                {deals.map(deal => (
                  <PreviewDealCard key={deal.id} deal={deal} />
                ))}
              </div>
            ) : null}

            <div className="mt-5 text-center">
              <Link
                href={`/${UTM}`}
                className="inline-block bg-gray-900 text-white text-sm font-semibold px-6 py-3 rounded-xl hover:bg-gray-700 transition-colors"
              >
                Bekijk alle deals →
              </Link>
            </div>
          </section>

          {/* ── Bottom repeat CTA ─────────────────────────────────────────── */}
          {!currentUser && (
            <div className="text-center pb-4">
              <p className="text-sm text-gray-500 mb-3">Klaar om alle deals te zien?</p>
              <button
                onClick={() => handleSignupCTA('bottom')}
                className="bg-blue-600 text-white font-semibold px-8 py-3 rounded-xl hover:bg-blue-700 transition-colors text-sm"
              >
                Gratis account aanmaken
              </button>
            </div>
          )}

        </div>
      </main>
    </>
  )
}
